import {
  BlockLabel,
  colors2,
  getVal,
  icons,
  List,
  setVal,
  TextInput,
  UI_BODY_SEMIBOLD_SM,
  UI_BODY_SM_DF,
  UI_TAG,
  UI_HELPTEXT,
} from "oolib";
import React, { useEffect, useRef, useState, useMemo } from "react";

import { DynamicOptionsLightboxShell } from "@/components/inputs/AccordionMultiSelect/comps/OptionsLightbox/DynamicOptionsLightboxShell";
import { CascadeFieldPicker } from "@/components/DataFieldInputSelector/CascadeFieldPicker";
import { SimpleTable } from "@/components/SimpleTable";
import { ButtonGhost } from "oolib/dist/v2/components/Buttons";
import styled, { css } from 'styled-components';
import { getHiddenDim1ForPreset, getHiddenDim2ForPreset } from "./utils/getTopBottomDatapoints";
import {
  getVisibilityKeys,
  readVisibilityState,
  resolveHidden,
  writeManualSelection,
  writeDynamicSettings,
  writeMode,
} from "../utils/visibilityState";
import {
  normalizeDisplayValue,
  normalizeValue,
  getHiddenItemValue,
} from "./utils/hiddenDatapoints";
import { ShowHideOptions } from "./ConfigForm/ShowHideOptions";
import { useReportLandingContext } from "../../../ReportLandingContext";
const {CaretRight} = icons;

export const StyledLightboxInputButton = styled.button`
 border: 1px solid ${colors2.grey10};
 background: none;
 border-radius: 6px;
 padding: 8px 10px;
 display: flex;
 justify-content: space-between;
 align-items: center;
 gap: 4px;
 text-align: left;
 cursor: pointer;
 width: 100%;
 ${({value, $multi}) => {
   // For multi-select, check if array is empty; for single-select, check if no display
   const isEmpty = $multi ? (!value || value.length === 0) : !value?.display;
   return isEmpty && css`color: ${colors2.grey60};`;
 }}

 &:disabled {
    border-color: ${colors2.grey10};
    color: ${colors2.grey40};
    cursor: not-allowed;
 }
`;

export const StyledWidgetHeaderWrapper = styled.div`
  background-color:  rgba(7, 118, 237, 0.05);
  border: 1px solid ${colors2.grey10};
  padding: 8px 12px 8px 10px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.6rem;
`

export const ReportsLightboxInput = ({
  id,
  label,
  options,
  value,
  onChange,
  disabled,
  placeholder = 'Select Option',
  multi = false,  // New prop for multi-select mode
  picker  // opt-in: { variant:'cascade' }. Cascade only for single-select.
}) => {

  const [expand, setExpand] = useState(false)
  const buttonRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('')

  const filterOptionsBasedOnSearchTerm = (data, searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') {
      return data; // Return all data if no search term
    }

    const term = searchTerm.toLowerCase().trim();

    return data.reduce((acc, item) => {
      // Check if item has options (nested structure)
      if (item.options && Array.isArray(item.options)) {
        // Handle nested structure
        const outerDisplay = (item.display || '').toLowerCase();
        const outerDesc = (item.desc || '').toLowerCase();

        // Check if outer item matches
        const outerMatches = outerDisplay.includes(term) || outerDesc.includes(term);

        // Filter inner options
        const filteredOptions = item.options.filter(option => {
          const innerDisplay = (option.display || '').toLowerCase();
          const innerDesc = (option.desc || '').toLowerCase();
          return innerDisplay.includes(term) || innerDesc.includes(term);
        });

        // Include item if outer matches OR if any inner options match
        if (outerMatches || filteredOptions.length > 0) {
          acc.push({
            ...item,
            options: outerMatches ? item.options : filteredOptions // If outer matches, show all options; otherwise show filtered
          });
        }
      } else {
        // Handle flat structure
        const display = (item.display || '').toLowerCase();
        const desc = (item.desc || '').toLowerCase();

        if (display.includes(term) || desc.includes(term)) {
          acc.push(item);
        }
      }

      return acc;
    }, []);
  };

  // Handle option change - works for both single and multi-select
  const handleOptionChange = (id, option) => {
    if (multi) {
      // Multi-select: toggle selection in array
      const currentValue = Array.isArray(value) ? value : [];
      const isSelected = currentValue.some(v => v.value === option.value);
      const newValue = isSelected
        ? currentValue.filter(v => v.value !== option.value)
        : [...currentValue, option];
      onChange(null, newValue);
      // Don't close lightbox in multi-select mode
    } else {
      // Single-select: pass the option and close
      onChange(null, option);
      setExpand(false);
    }
  };

  // Get display text for the button
  const getButtonDisplayText = () => {
    if (multi) {
      if (!value || !Array.isArray(value) || value.length === 0) {
        return placeholder;
      }
      // Show comma-separated display names
      return value.map(v => v.display || v.displayName).join(', ');
    }
    return value?.display || placeholder;
  };

  const ref_reportsLightboxInputHeader = useRef(null);
  const [listGroupHeaderTop, setListGroupHeaderTop] = useState(0);
  useEffect(() => {

    const headerHeight = ref_reportsLightboxInputHeader.current?.getBoundingClientRect()?.height;


    if(expand && headerHeight){
      setListGroupHeaderTop(headerHeight + 'px')
    }
  },[expand])

  return (
    <div>
      <BlockLabel label={label}/>
      <StyledLightboxInputButton value={value} $multi={multi} disabled={disabled} ref={buttonRef} onClick={() => setExpand(true)}>
        <UI_BODY_SM_DF style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {getButtonDisplayText()}
        </UI_BODY_SM_DF>
        <CaretRight size={16} style={{flexShrink: 0}}/>
      </StyledLightboxInputButton>
      {expand && (picker?.variant === 'cascade' && !multi) && (
        <CascadeFieldPicker
          anchorEl={buttonRef.current}
          open
          onOpenChange={(v) => { if (!v) { setExpand(false); setSearchTerm(''); } }}
          options={options}
          value={value}
          // Route through the SAME single-select handler the flat List uses, so
          // the emitted option (and thus stored config) is byte-identical.
          onChange={(opt) => handleOptionChange(null, opt)}
        />
      )}
      {expand && !(picker?.variant === 'cascade' && !multi) && <DynamicOptionsLightboxShell
      onClickOutside={() => { setExpand(false); setSearchTerm(''); }}
      ref_anchorPoint={buttonRef}
      isMobile={false}
      width={{
        desktop: 300,
        mobile: 300
      }}
        >
          <div>
            <div ref={ref_reportsLightboxInputHeader}  style={{position: 'sticky', top: 0, background: colors2.white, zIndex: 10000 /* TODO: Use Z_INDEX.WIDGET_CONFIG_STICKY_HEADER from styled.js - so that the sticky title from the List goes under this */}}>
            <div style={{padding: '2px 10px', borderBottom: `1px solid ${colors2.grey10}`, display: 'flex', justifyContent: 'flex-end'}}>
              <ButtonGhost icon='X' onClick={() => { setExpand(false); setSearchTerm(''); }}/>
            </div>
            <div style={{padding: '10px'}}>
          <TextInput
            S
            value={searchTerm}
            onChange={(_,v) => setSearchTerm(v)}
            placeholder='Search'
            />
            </div>
            </div>

            <List
              groupHeaderStickyTop={listGroupHeaderTop}
              grouped
              disableShadow
              isMulti={multi}
              options={filterOptionsBasedOnSearchTerm(options, searchTerm)}
              value={value}
              onChange={handleOptionChange}
            />
          </div>
        </DynamicOptionsLightboxShell>}

    </div>
  )
}

export const WidgetHeader = ({ title, type, icon }) => {

  const CompIcon = icon && icons[icon] ? icons[icon] : null;

  return(
  <StyledWidgetHeaderWrapper >
    {CompIcon && <CompIcon size={36} style={{ color: colors2.primaryContainer }} fill={colors2.primaryContainer}/>}
    <div>
      <UI_TAG style={{ color: colors2.grey50 }}>Widget Type</UI_TAG>
      <div style={{ display: "flex", alignItems: "center", color: colors2.black }}>
          {type &&
            <>
            <UI_BODY_SEMIBOLD_SM>{type}</UI_BODY_SEMIBOLD_SM>
            <CaretRight size={14}/>
            </>
            }
          <UI_BODY_SEMIBOLD_SM>{title}</UI_BODY_SEMIBOLD_SM>
      </div>
    </div>
  </StyledWidgetHeaderWrapper>
  )
}

/**
 * Validate custom ranges for errors
 * Returns an array of error messages, empty if valid
 */
export const validateCustomRanges = (tableData) => {
  if (!tableData?.data || !Array.isArray(tableData.data)) {
    return [];
  }

  const errors = [];
  const ranges = [];

  // Extract and validate individual ranges
  tableData.data.forEach((row, idx) => {
    if (!row.cellData || row.cellData.length < 2) return;

    const minVal = parseFloat(row.cellData[0]?.value);
    const maxVal = parseFloat(row.cellData[1]?.value);

    // Skip empty rows
    if (isNaN(minVal) && isNaN(maxVal)) return;

    // Check if only one value is filled
    if (isNaN(minVal) || isNaN(maxVal)) {
      errors.push(`Row ${idx + 1}: Both min and max values are required`);
      return;
    }

    // Check if min >= max
    if (minVal >= maxVal) {
      errors.push(`Row ${idx + 1}: Min (${minVal}) must be less than Max (${maxVal})`);
      return;
    }

    ranges.push({ min: minVal, max: maxVal, rowIdx: idx + 1 });
  });

  // Check for overlapping ranges
  if (ranges.length > 1) {
    // Sort by min value
    const sortedRanges = [...ranges].sort((a, b) => a.min - b.min);

    for (let i = 0; i < sortedRanges.length - 1; i++) {
      const current = sortedRanges[i];
      const next = sortedRanges[i + 1];

      // Check if current range overlaps with next range
      // Overlap occurs if current.max > next.min
      if (current.max > next.min) {
        errors.push(`Ranges overlap: "${current.min}-${current.max}" overlaps with "${next.min}-${next.max}"`);
      }
    }
  }

  return errors;
};

/**
 * Helper component to render SimpleTable for custom number ranges
 * when a NumberInput dimension is selected
 */
export const CustomRangesInput = ({
  dimensionKey,
  dimensionValue,
  customRangesKey,
  thisWidgetConfig,
  setConfig,
  underEditWidgetIdx
}) => {
  const isNumberInput = dimensionValue?.comp === 'NumberInput';

  const currentRangesData = getVal(thisWidgetConfig, customRangesKey);

  // Validate ranges and memoize the result
  const validationErrors = useMemo(() => {
    return validateCustomRanges(currentRangesData);
  }, [currentRangesData]);

  // Only show for NumberInput dimensions
  if (!isNumberInput) {
    return null;
  }

  const handleRangesChange = (tableId, newTableData) => {
    setConfig((prev) => {
      let modifiedConfig = JSON.parse(JSON.stringify(prev[underEditWidgetIdx]));
      modifiedConfig = setVal(modifiedConfig, customRangesKey, newTableData);
      const modifiedFullConfig = JSON.parse(JSON.stringify(prev));
      modifiedFullConfig[underEditWidgetIdx] = modifiedConfig;
      return modifiedFullConfig;
    });
  };

  return (
    <div style={{ marginTop: '0.8rem' }}>
      <SimpleTable
        label="Custom Number Ranges (Optional)"
        sublabel="Define custom ranges for grouping numeric values. Leave empty to use auto-generated ranges."
        id={`customRanges_${dimensionKey}_${underEditWidgetIdx}`}
        config={{
          noOfRows: 2,
          noOfCols: 2,
          colHeaderData: ['Min', 'Max'],
          blockConfig: [
            { comp: 'NumberInput', props: { placeholder: "Min value" } },
            { comp: 'NumberInput', props: { placeholder: "Max value" } }
          ],
          disableAddRow: false,
          disableRowActionsMenu: false,
          disablePrimaryActionsMenu: true
        }}
        value={currentRangesData}
        onChange={handleRangesChange}
        defaultColWidth={120}
      />
      {validationErrors.length > 0 && (
        <div style={{
          marginTop: '0.5rem',
          padding: '0.5rem 0.75rem',
          backgroundColor: colors2.redBG,
          borderRadius: '4px'
        }}>
          {validationErrors.map((error, idx) => (
            <UI_HELPTEXT key={idx} style={{ color: colors2.red, display: 'block' }}>
              {error}
            </UI_HELPTEXT>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Helper component to render a number input for configuring
 * how many auto-generated buckets to create for NumberInput dimensions
 */
export const NumberOfBucketsInput = ({
  dimensionValue,
  numberOfBucketsKey,
  thisWidgetConfig,
  setConfig,
  underEditWidgetIdx
}) => {
  const isNumberInput = dimensionValue?.comp === 'NumberInput';

  // Don't show if custom ranges are already defined (custom ranges override bucket count)
  if (!isNumberInput) {
    return null;
  }

  const currentValue = getVal(thisWidgetConfig, numberOfBucketsKey);

  const handleChange = (id, val) => {
    setConfig((prev) => {
      let modifiedConfig = JSON.parse(JSON.stringify(prev[underEditWidgetIdx]));
      const numVal = val ? parseInt(val, 10) : undefined;
      if (numVal && numVal >= 1 && numVal <= 50) {
        modifiedConfig = setVal(modifiedConfig, numberOfBucketsKey, numVal);
      } else if (!val) {
        modifiedConfig = setVal(modifiedConfig, numberOfBucketsKey, undefined);
      }
      const modifiedFullConfig = JSON.parse(JSON.stringify(prev));
      modifiedFullConfig[underEditWidgetIdx] = modifiedConfig;
      return modifiedFullConfig;
    });
  };

  return (
    <div style={{ marginTop: '0.8rem' }}>
      <TextInput
        label="Number of Buckets"
        sublabel="How many auto-generated ranges to create (1–50). Defaults to 5. Ignored when custom ranges are defined."
        id={`numberOfBuckets_${underEditWidgetIdx}`}
        type="number"
        value={currentValue ?? ''}
        onChange={handleChange}
        placeholder="5"
      />
    </div>
  );
};

/**
 * Component for showing/hiding datapoints on the chart.
 * Uses the ShowHideOptions lightbox with Manual + Dynamic tabs.
 *
 * - dimension=1: Show/hide entire bars (top-level display values)
 * - dimension=2: Show/hide breakdown segments (children display values)
 *
 * Architecture: the manual selection and the dynamic settings are stored
 * independently (see utils/visibilityState) and the applied list is derived at
 * render time, so switching modes never destroys either one.
 */
export const DatapointVisibilityInput = ({
  dimension, // 1 or 2
  thisWidgetConfig,
  setConfig,
  underEditWidgetIdx,
  dataKeys
}) => {
  const { widgetDataStore } = useReportLandingContext();
  const data = widgetDataStore?.[thisWidgetConfig.id];

  const keys = useMemo(
    () => getVisibilityKeys({ dataKeys, dimension }),
    [dataKeys, dimension]
  );
  const state = useMemo(
    () => readVisibilityState(thisWidgetConfig, keys),
    [thisWidgetConfig, keys]
  );
  const computeHidden = (d, preset, n) =>
    dimension === 1
      ? getHiddenDim1ForPreset(d, preset, n)
      : getHiddenDim2ForPreset(d, preset, n);

  // Generate options from chart data — use value (unique ID) for option value
  const options = useMemo(() => {
    if (!data?.length) return [];
    // Skip datapoints with no caption (empty/null display) — they'd otherwise
    // show as a blank row in the Show/Hide list.
    if (dimension === 1) {
      return data.reduce((acc, d) => {
        const normalizedDisplay = normalizeDisplayValue(d.display);
        if (normalizedDisplay === "") return acc;
        acc.push({ display: normalizedDisplay, value: normalizeValue(d.value) });
        return acc;
      }, []);
    } else {
      const optionsMap = new Map();
      data.forEach(d => {
        if (d.children?.length) {
          d.children.forEach(child => {
            const normalizedDisplay = normalizeDisplayValue(child.display);
            if (normalizedDisplay === "") return;
            const normalizedVal = normalizeValue(child.value);
            if (!optionsMap.has(normalizedVal)) {
              optionsMap.set(normalizedVal, {
                display: normalizedDisplay,
                value: normalizedVal,
              });
            }
          });
        }
      });
      return Array.from(optionsMap.values());
    }
  }, [data, dimension]);

  const effectiveHiddenValues = useMemo(
    () => resolveHidden({ state, data, dimension }),
    [state, data, dimension]
  );

  if (!data?.length) return null;
  if (dimension === 2 && options.length === 0) return null;

  const updateConfig = (updaterFn) => {
    setConfig((prev) => {
      let w = JSON.parse(JSON.stringify(prev[underEditWidgetIdx]));
      w = updaterFn(w);
      const full = [...prev];
      full[underEditWidgetIdx] = w;
      return full;
    });
  };

  const handleManualChange = (newHidden) => {
    updateConfig((w) => writeManualSelection(w, keys, newHidden));
  };

  const handleDynamicChange = (dynamicPreset, n) => {
    updateConfig((w) => writeDynamicSettings(w, keys, { dynamicPreset, n }));
  };

  const handleModeChange = (mode) => {
    updateConfig((w) => {
      return writeMode(w, keys, mode);
    });
  };

  return (
    <ShowHideOptions
      options={options}
      hiddenValues={effectiveHiddenValues}
      mode={state.mode}
      dynamicPreset={state.dynamicPreset}
      nValue={state.n}
      onManualChange={handleManualChange}
      onDynamicChange={handleDynamicChange}
      onModeChange={handleModeChange}
    />
  );
};
